/**
 * Watch a source the catalogue does not know — a service provider's own
 * rules, an industry body, a regulator we have not catalogued. Name, issuer,
 * the pages to read, how to read them, and which of the organization's
 * frameworks it watches on behalf of.
 */
import { useState } from "react";

import { useFrameworks, type MonitorCreateInput } from "../../../state/governance";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";

export function AddMonitorDialog({
  organizationId,
  open,
  onOpenChange,
  onCreate,
}: {
  readonly organizationId: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreate: (input: MonitorCreateInput) => Promise<string | null>;
}) {
  const { snapshot } = useFrameworks(organizationId);
  const enabled = (snapshot?.frameworks ?? []).filter((framework) => framework.enabled);
  const [name, setName] = useState("");
  const [issuer, setIssuer] = useState("");
  const [urls, setUrls] = useState("");
  const [browser, setBrowser] = useState(false);
  const [frameworkIds, setFrameworkIds] = useState<ReadonlyArray<string>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setIssuer("");
    setUrls("");
    setBrowser(false);
    setFrameworkIds([]);
    setError(null);
  };

  const sourceUrls = urls
    .split(/\s+/)
    .map((value) => value.trim())
    .filter((value) => /^https?:\/\//.test(value));

  const submit = async () => {
    if (name.trim().length < 3 || issuer.trim().length < 2) {
      setError("Give the source a name and say who publishes it.");
      return;
    }
    if (sourceUrls.length === 0) {
      setError("At least one page to read, as a full URL.");
      return;
    }
    setBusy(true);
    setError(null);
    const message = await onCreate({
      name: name.trim(),
      issuer: issuer.trim(),
      sourceUrls,
      access: browser ? "browser" : "fetch",
      frameworkIds,
    });
    setBusy(false);
    if (message !== null) {
      setError(message);
      return;
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a source to watch</DialogTitle>
          <DialogDescription>
            For a publisher the catalogue does not know — a service provider's own rules, an
            industry body. The weekly inspection reads it like any regulator.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Name</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Apex Group — client money rules"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Who publishes it</span>
            <Input
              value={issuer}
              onChange={(event) => setIssuer(event.target.value)}
              placeholder="e.g. Apex Group"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Pages to read, one per line</span>
            <Textarea
              value={urls}
              onChange={(event) => setUrls(event.target.value)}
              placeholder="https://…"
              rows={3}
              className="resize-none font-mono text-xs"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={browser}
              onChange={(event) => setBrowser(event.target.checked)}
            />
            Needs a browser (the site blocks plain fetches)
          </label>
          <fieldset className="space-y-1.5">
            <legend className="text-xs font-medium text-muted-foreground">Watches on behalf of</legend>
            {enabled.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No frameworks enabled yet — enable one on the Frameworks page.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {enabled.map((framework) => {
                  const on = frameworkIds.includes(framework.id);
                  return (
                    <button
                      key={framework.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setFrameworkIds(
                          on
                            ? frameworkIds.filter((id) => id !== framework.id)
                            : [...frameworkIds, framework.id],
                        )
                      }
                      className={
                        on
                          ? "cursor-pointer rounded-full bg-foreground px-2.5 py-1 text-xs text-background"
                          : "cursor-pointer rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
                      }
                    >
                      {framework.name}
                    </button>
                  );
                })}
              </div>
            )}
          </fieldset>
          {error !== null && <p className="text-xs text-destructive">{error}</p>}
        </DialogPanel>
        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={busy}
            className="cursor-pointer"
          >
            {busy ? "Working…" : "Watch"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
